import { BeddingRequest, Event, EventBeddingType, EventEntry, EventEntryFee, EventFee, EventStallType, Fee, Person, StablingRequest } from "../../models";
import { getBeddingRequestsByPersonIdEventId } from "../beddingRequest/BeddingRequest";
import { getEventBeddingTypeById } from "../eventBeddingType/EventBeddingType";
import { getEventEntryFeesByEventId, getEventEntryFeesByEventIdByEntryId } from "../eventEntryFee/EventEntryFee";
import { formatSingleEventFeeAsStablingAndExtrasItem } from "../eventFee/FormatEventFeeOptionsUtil";
import { StablingAndExtrasItem } from "../eventStallType/FormatStablingOptionsUtil";
import { getStablingRequestsByPersonIdEventId } from "./StablingRequest";

/** Stabling/bedding requests with no `submittedEntry` are stored on the shared Barn Account. */
export function isBarnAccountSubmittedEntry(submittedEntry?: string | null): boolean {
    return submittedEntry == null || String(submittedEntry).trim() === "";
}

export interface BarnContextFromEntries {
    barnId: string;
    barnName: string;
    trainerId?: string | null;
    entryIds: string[];
}

/** Entries grouped by `barnId` for one Barn Account subsection per barn. */
export interface BarnEntryGroup {
    barnId: string;
    barnName: string;
    trainerId?: string | null;
    entries: EventEntry[];
}

export function barnAccountIdForBarn(barnId: string): string {
    return `barn-account-${barnId}`;
}

/** @deprecated Prefer `groupCompletedEntriesByBarn` when multiple barns may be present. */
export function resolveBarnContextFromEntries(currentEventEntries: EventEntry[]): BarnContextFromEntries | null {
    const groups = groupCompletedEntriesByBarn(currentEventEntries);
    if (groups.length === 0) return null;
    const primary = groups[0];
    return {
        barnId: primary.barnId,
        barnName: primary.barnName,
        trainerId: primary.trainerId,
        entryIds: primary.entries.map((e) => e.id).filter(Boolean) as string[],
    };
}

export function groupCompletedEntriesByBarn(currentEventEntries: EventEntry[]): BarnEntryGroup[] {
    const completedWithBarn = currentEventEntries.filter((e) => e.barnId);
    const byBarnId = new Map<string, BarnEntryGroup>();

    for (const entry of completedWithBarn) {
        const barnId = entry.barnId!;
        const barnName = entry.barnName?.trim() || entry.barn?.name?.trim() || "Barn Account";
        const existing = byBarnId.get(barnId);
        if (existing) {
            existing.entries.push(entry);
        } else {
            byBarnId.set(barnId, {
                barnId,
                barnName,
                trainerId: entry.trainerId || entry.trainer?.id || null,
                entries: [entry],
            });
        }
    }

    const groups = Array.from(byBarnId.values());
    for (const group of groups) {
        group.entries.sort((a, b) => (a.horseName || "").localeCompare(b.horseName || "", undefined, { sensitivity: "base" }));
    }
    groups.sort((a, b) => a.barnName.localeCompare(b.barnName, undefined, { sensitivity: "base" }));
    return groups;
}

const BARN_FEE_BARN_TAG_PREFIX = "__barnAccount:";

export function barnAccountFeeDescription(barnId: string, description?: string | null): string {
    const tag = `${BARN_FEE_BARN_TAG_PREFIX}${barnId}`;
    const base = description?.trim();
    return base ? `${base} ${tag}` : tag;
}

function feeMatchesBarnAccount(fee: EventEntryFee, barnId: string, isOnlyBarnGroup: boolean): boolean {
    if (fee.entryId) return false;
    const tag = `${BARN_FEE_BARN_TAG_PREFIX}${barnId}`;
    if (fee.description?.includes(tag)) return true;
    // Legacy fees saved before per-barn tagging — attach to the sole barn group only.
    return isOnlyBarnGroup && !fee.description?.includes(BARN_FEE_BARN_TAG_PREFIX);
}

export interface FormattedBarnAccountData {
    id: string;
    barnId: string;
    barnName: string;
    rows: FormattedRow[];
}

export interface FormattedRow {
    item: StablingAndExtrasItem;
    quantity: number;
}

/** @deprecated Use `barnAccountIdForBarn(barnId)` — one barn account block per barn. */
export const BARN_ACCOUNT_ID = "barn-account";

export interface FormattedEntryData {
    id: string;
    title: string;
    eventEntry: EventEntry;
    rows: FormattedRow[];
    /** Shared barn-level purchases (stalls, bedding, extras) not tied to one entry. */
    isBarnAccount?: boolean;
    barnId?: string;
    barnName?: string;
    /** Completed entries stabled with this barn (shown under the barn account header). */
    linkedEntries?: EventEntry[];
}

export function isBarnAccountEntry(data: FormattedEntryData): boolean {
    return data.isBarnAccount === true || data.id === BARN_ACCOUNT_ID || data.id.startsWith("barn-account-");
}

/** Stabling/bedding rows saved to the barn account use no submittedEntry (split later by show office). */
export function isBarnAccountStablingOrBeddingRequest(request: { submittedEntry?: string | null }): boolean {
    const submitted = request.submittedEntry;
    return submitted == null || submitted === "";
}

function formatRowsFromPreviousStablingRequests(requests: StablingRequest[]): FormattedRow[] {
    const formattedRows: FormattedRow[] = [];
    for (let j = 0; j < requests.length; j++) {
        const previousStablingRequest = requests[j];
        const eventStallType: EventStallType | null | undefined = previousStablingRequest.stallType;
        if (eventStallType) {
            const stablingItem: StablingAndExtrasItem = {
                value: eventStallType.id,
                label: eventStallType.name || "Unknown Name",
                cost: eventStallType.pricePerStall || "Unknown Price",
                description: eventStallType.description || "",
                eventStallType: eventStallType,
                previousStablingRequest: previousStablingRequest,
            };
            formattedRows.push({
                item: stablingItem,
                quantity: previousStablingRequest.quantityNeeded || 0,
            });
        }
    }
    return formattedRows;
}

async function formatRowsFromPreviousBeddingRequests(requests: BeddingRequest[]): Promise<FormattedRow[]> {
    const formattedRows: FormattedRow[] = [];
    for (let k = 0; k < requests.length; k++) {
        const previousBeddingRequest = requests[k];
        const eventBeddingTypeId: string | null | undefined = previousBeddingRequest.beddingType;
        let eventBeddingType: EventBeddingType | null | undefined = null;
        if (eventBeddingTypeId) {
            const eventBeddingTypeQuery = await getEventBeddingTypeById(eventBeddingTypeId);
            if (eventBeddingTypeQuery.isSuccess) {
                eventBeddingType = eventBeddingTypeQuery.result;
            }
        }

        if (eventBeddingType) {
            const beddingItem: StablingAndExtrasItem = {
                value: eventBeddingType?.id,
                label: eventBeddingType?.name || "Unknown Name",
                cost: eventBeddingType.pricePerBag || "Unknown Price",
                description: eventBeddingType.description || "",
                eventBeddingType: eventBeddingType,
                eventBeddingTypeId: previousBeddingRequest.beddingType || "",
                previousBeddingRequest: previousBeddingRequest,
            };
            formattedRows.push({
                item: beddingItem,
                quantity: previousBeddingRequest.quantityNeeded || 0,
            });
        }
    }
    return formattedRows;
}

function formatRowsFromPreviousEventEntryFees(fees: EventEntryFee[]): FormattedRow[] {
    const formattedRows: FormattedRow[] = [];
    for (let l = 0; l < fees.length; l++) {
        const previousEventEntryFee = fees[l];
        const eventFee: EventFee | null | undefined = previousEventEntryFee.eventFee;
        if (eventFee) {
            const fee: Fee | null | undefined = previousEventEntryFee.fee;
            let formattedEventFee: StablingAndExtrasItem = formatSingleEventFeeAsStablingAndExtrasItem(eventFee, fee || undefined);
            formattedEventFee.previousEventEntryFee = previousEventEntryFee;
            formattedRows.push({
                item: formattedEventFee,
                quantity: previousEventEntryFee.quantity || 0,
            });
        }
    }
    return formattedRows;
}

export async function getPreviousStablingRequests(user: Person, event: Event) {
    let currentUserPreviousStablingRequests: StablingRequest[] = [];
    const queryResult = await getStablingRequestsByPersonIdEventId(user.id, event.id);
    if (queryResult.isSuccess) {
        currentUserPreviousStablingRequests = queryResult.result;
    }
    return currentUserPreviousStablingRequests;
}

export async function getPreviousBeddingRequests(user: Person, event: Event) {
    let currentUserPreviousBeddingRequests: BeddingRequest[] = [];
    const queryResult = await getBeddingRequestsByPersonIdEventId(user.id, event.id);
    if (queryResult.isSuccess) {
        currentUserPreviousBeddingRequests = queryResult.result;
    }
    return currentUserPreviousBeddingRequests;
}

export const getFormattedBarnAccountData = async (event: Event, user: Person, barnContext: BarnContextFromEntries): Promise<FormattedBarnAccountData> => {
    const previousStablingRequests = await getPreviousStablingRequests(user, event);
    const previousBeddingRequests = await getPreviousBeddingRequests(user, event);

    const barnStabling = previousStablingRequests.filter((sr) => isBarnAccountSubmittedEntry(sr.submittedEntry) && sr.barnId === barnContext.barnId);
    const barnBedding = previousBeddingRequests.filter((br) => isBarnAccountSubmittedEntry(br.submittedEntry) && br.barnId === barnContext.barnId);

    const formattedRows: FormattedRow[] = [];

    for (const previousStablingRequest of barnStabling) {
        const eventStallType: EventStallType | null | undefined = previousStablingRequest.stallType;
        if (eventStallType) {
            formattedRows.push({
                item: {
                    value: eventStallType.id,
                    label: eventStallType.name || "Unknown Name",
                    cost: eventStallType.pricePerStall || "Unknown Price",
                    description: eventStallType.description || "",
                    eventStallType,
                    previousStablingRequest,
                },
                quantity: previousStablingRequest.quantityNeeded || 0,
            });
        }
    }

    for (const previousBeddingRequest of barnBedding) {
        const eventBeddingTypeId = previousBeddingRequest.beddingType;
        let eventBeddingType: EventBeddingType | null | undefined = null;
        if (eventBeddingTypeId) {
            const eventBeddingTypeQuery = await getEventBeddingTypeById(eventBeddingTypeId);
            if (eventBeddingTypeQuery.isSuccess) {
                eventBeddingType = eventBeddingTypeQuery.result;
            }
        }
        if (eventBeddingType) {
            formattedRows.push({
                item: {
                    value: eventBeddingType.id,
                    label: eventBeddingType.name || "Unknown Name",
                    cost: eventBeddingType.pricePerBag || "Unknown Price",
                    description: eventBeddingType.description || "",
                    eventBeddingType,
                    eventBeddingTypeId: previousBeddingRequest.beddingType || "",
                    previousBeddingRequest,
                },
                quantity: previousBeddingRequest.quantityNeeded || 0,
            });
        }
    }

    const feesQuery = await getEventEntryFeesByEventId(event.id);
    if (feesQuery.isSuccess) {
        const barnFees = feesQuery.result.filter(
            (fee: EventEntryFee) => fee.createdBy === user.id && feeMatchesBarnAccount(fee, barnContext.barnId, true)
        );
        for (const previousEventEntryFee of barnFees) {
            const eventFee: EventFee | null | undefined = previousEventEntryFee.eventFee;
            if (eventFee) {
                const fee: Fee | null | undefined = previousEventEntryFee.fee;
                let formattedEventFee: StablingAndExtrasItem = formatSingleEventFeeAsStablingAndExtrasItem(eventFee, fee || undefined);
                formattedEventFee.previousEventEntryFee = previousEventEntryFee;
                formattedRows.push({
                    item: formattedEventFee,
                    quantity: previousEventEntryFee.quantity || 0,
                });
            }
        }
    }

    return {
        id: `barn-account-${barnContext.barnId}`,
        barnId: barnContext.barnId,
        barnName: barnContext.barnName,
        rows: formattedRows,
    };
};

export const getFormattedEntryData = async (event: Event, user: Person, currentEventEntries: EventEntry[]) => {
    const formattedEntries: FormattedEntryData[] = [];
    const eventEntries = currentEventEntries;
    if (eventEntries.length === 0) {
        return formattedEntries;
    }

    const previousStablingRequests = await getPreviousStablingRequests(user, event);
    const previousBeddingRequests = await getPreviousBeddingRequests(user, event);

    const barnGroups = groupCompletedEntriesByBarn(currentEventEntries);
    const isOnlyBarnGroup = barnGroups.length === 1;

    let allBarnEventEntryFees: EventEntryFee[] = [];
    const eventFeesQuery = await getEventEntryFeesByEventId(event.id);
    if (eventFeesQuery.isSuccess) {
        allBarnEventEntryFees = eventFeesQuery.result.filter((fee: EventEntryFee) => fee.createdBy === user.id && !fee.entryId);
    }

    for (const barnGroup of barnGroups) {
        const barnStablingRequests = previousStablingRequests.filter(
            (sr: StablingRequest) => isBarnAccountStablingOrBeddingRequest(sr) && sr.barnId === barnGroup.barnId
        );
        const barnBeddingRequests = previousBeddingRequests.filter(
            (br: BeddingRequest) => isBarnAccountStablingOrBeddingRequest(br) && br.barnId === barnGroup.barnId
        );
        const barnEventEntryFees = allBarnEventEntryFees.filter((fee) => feeMatchesBarnAccount(fee, barnGroup.barnId, isOnlyBarnGroup));

        const barnRows: FormattedRow[] = [
            ...formatRowsFromPreviousStablingRequests(barnStablingRequests),
            ...(await formatRowsFromPreviousBeddingRequests(barnBeddingRequests)),
            ...formatRowsFromPreviousEventEntryFees(barnEventEntryFees),
        ];

        formattedEntries.push({
            id: barnAccountIdForBarn(barnGroup.barnId),
            title: barnGroup.barnName,
            isBarnAccount: true,
            barnId: barnGroup.barnId,
            barnName: barnGroup.barnName,
            linkedEntries: barnGroup.entries,
            eventEntry: barnGroup.entries[0],
            rows: barnRows,
        });
    }

    for (let i = 0; i < eventEntries.length; i++) {
        const current = eventEntries[i];
        const title = "Entry " + (i + 1).toString() + " - " + current.horseName;

        const previousStablingRequestsForCurrentEntry = previousStablingRequests.filter(
            (sr: StablingRequest) => sr.submittedEntry === current.id && !isBarnAccountSubmittedEntry(sr.submittedEntry)
        );
        const previousBeddingRequestsForCurrentEntry = previousBeddingRequests.filter(
            (br: BeddingRequest) => br.submittedEntry === current.id && !isBarnAccountSubmittedEntry(br.submittedEntry)
        );

        let previousEventEntryFeesForCurrentEntry: EventEntryFee[] = [];
        const previousEventEntryFeesForCurrentEntryQuery = await getEventEntryFeesByEventIdByEntryId(event.id, current.id);
        if (previousEventEntryFeesForCurrentEntryQuery.isSuccess) {
            previousEventEntryFeesForCurrentEntry = previousEventEntryFeesForCurrentEntryQuery.result;
        }

        const formattedRows: FormattedRow[] = [
            ...formatRowsFromPreviousStablingRequests(previousStablingRequestsForCurrentEntry),
            ...(await formatRowsFromPreviousBeddingRequests(previousBeddingRequestsForCurrentEntry)),
            ...formatRowsFromPreviousEventEntryFees(previousEventEntryFeesForCurrentEntry),
        ];

        formattedEntries.push({
            id: current.id,
            title: title,
            eventEntry: current,
            rows: formattedRows,
        });
    }
    return formattedEntries.filter((d) => d.id !== BARN_ACCOUNT_ID);
};
